import { useTranslation } from "react-i18next";
import crudApi from "@serie3/common/api/crudApi";
import NewModal from "../../../shared/Modals/NewModal";
import { useEffect, useState } from "react";
import { Row, Col, Button } from "react-bootstrap";
import AiPlanningAssistant from "@serie3/mymediaplanner/Modules/Social/Forms/AiPlanningAssistant";
import { useFormik } from "formik";
import DateField from "../../../Form/DateField";
import NumberField from "../../../Form/NumberField";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faLayerGroup, faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons";
import PostCalendar from "../../PostCalendar";
import listHandler from "@serie3/common/Domain/listHandler";
import Timezones from "../../../Form/Timezones";

const PILLAR_COLORS = ["#038edc", "#51d28c", "#f1734f", "#564ab1", "#f7cc53", "#5fd0f3"];

const STEPS = [
  { label: "Month" },
  { label: "Interval" },
  { label: "Days" },
  { label: "Pillars" },
  { label: "Review" },
];

const StepBar = ({ current, total }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 28 }}>
    {Array.from({ length: total }).map((_, i) => {
      const done = i < current;
      const active = i === current;
      return (
        <div key={i} style={{ display: "flex", alignItems: "center", flex: i < total - 1 ? 1 : "none" }}>
          <div style={{
            width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: done ? "#51d28c" : active ? "#038edc" : "#eaecf0",
            color: done || active ? "#fff" : "#9299a8",
            fontSize: 11, fontWeight: 700,
            transition: "background 0.2s",
          }}>
            {done ? <FontAwesomeIcon icon={faCheck} style={{ fontSize: 10 }} /> : i + 1}
          </div>
          {i < total - 1 && (
            <div style={{
              flex: 1, height: 2,
              background: done ? "#51d28c" : "#eaecf0",
              transition: "background 0.2s",
            }} />
          )}
        </div>
      );
    })}
  </div>
);

const SectionLabel = ({ children }) => (
  <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "1.2px", textTransform: "uppercase", color: "#9299a8", marginBottom: 16 }}>
    {children}
  </p>
);

const AiPlanningAssistantView = ({ show, setShow, onPlanSaved }) => {
  const { t } = useTranslation();
  const [slideIndex, setSlideIndex] = useState(0);
  const [schedules, setSchedules] = useState([]);
  const [stepTitle, setStepTitle] = useState(t("Backend.Post.Assistant.AiPlanner.Titles.Month"));
  const [pillars, setPillars] = useState([]);
  const [selectedPillars, setSelectedPillars] = useState([]);
  const [fullScreen, setFullScreen] = useState(false);

  const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  const AiPlanningAssistantForm = AiPlanningAssistant.createAiPlanningAssistantForm(schedules, setSchedules);
  const formik = useFormik(AiPlanningAssistantForm);

  useEffect(() => {
    const titles = [
      t("Backend.Post.Assistant.AiPlanner.Titles.Month"),
      t("Backend.Post.Assistant.AiPlanner.Titles.Interval"),
      t("Backend.Post.Assistant.AiPlanner.Titles.Days"),
      t("Backend.Post.Assistant.AiPlanner.Titles.UsePillars"),
      t("Backend.Post.Assistant.AiPlanner.Titles.Sum"),
    ];
    setStepTitle(titles[slideIndex] || "");

    if (slideIndex === 3) {
      crudApi.getAll(SocialEndpoints.posts.contentPillars.index, setPillars, "contentpillars");
    }
  }, [slideIndex]);

  const savePlan = async () => {
    for (const schedule of schedules) {
      let postId = schedule.post.id ? schedule.post.id : null;
      if (!postId) {
        const response = await crudApi.createNew(SocialEndpoints.posts.new, {
          title: schedule.post.title,
          text: schedule.post.text,
          content_pillar: schedule.post.content_pillar ?? null,
        });
        if (response.ok) postId = response.data.id;
      }
      await crudApi.createNew(SocialEndpoints.posts.schedule.new, {
        post: { id: postId },
        local_user_time: schedule.scheduled_for?.slice(0, 16),
        timezone: "Europe/Berlin",
      });
    }
    onPlanSaved?.();
    setShow(false);
  };

  const deletePlan = (item) => {
    const deleteItem = { id: item.scheduleId };
    const updatedList = listHandler.deleteWithoutCall(deleteItem, schedules);
    setSchedules([...updatedList]);
  };

  const toggleDay = (day) => {
    const current = formik.values.days || [];
    if (current.includes(day)) {
      formik.setFieldValue("days", current.filter((d) => d !== day));
    } else if (current.length < formik.values.interval) {
      formik.setFieldValue("days", [...current, day]);
    }
  };

  const togglePillar = (pillar) => {
    const isSelected = selectedPillars.some((p) => p.id === pillar.id);
    const updated = isSelected
      ? selectedPillars.filter((p) => p.id !== pillar.id)
      : [...selectedPillars, pillar];
    setSelectedPillars(updated);
    formik.setFieldValue("pillars", updated);
  };

  const NavButtons = () => (
    <div style={{ display: "flex", gap: 8 }}>
      {slideIndex > 0 && slideIndex < 5 && (
        <button
          type="button"
          onClick={() => { setSlideIndex(slideIndex - 1); setFullScreen(false); }}
          style={{
            padding: "8px 20px", borderRadius: 50, border: "1.5px solid #e2e5ea",
            background: "#fff", color: "#374151", fontSize: 14, fontWeight: 600, cursor: "pointer",
          }}
        >
          ← {t("Backend.Post.Assistant.Back")}
        </button>
      )}
      {slideIndex === 0 && formik.values["month"] && (
        <button type="button" onClick={() => setSlideIndex(1)} style={nextBtnStyle}>
          {t("Backend.Buttons.Next")} →
        </button>
      )}
      {slideIndex === 1 && formik.values["interval"] > 0 && (
        <button type="button" onClick={() => setSlideIndex(2)} style={nextBtnStyle}>
          {t("Backend.Buttons.Next")} →
        </button>
      )}
      {slideIndex === 2 && (
        <button type="button" onClick={() => setSlideIndex(3)} style={nextBtnStyle}>
          {t("Backend.Buttons.Next")} →
        </button>
      )}
      {slideIndex === 3 && (
        <button type="button" onClick={() => setSlideIndex(4)} style={nextBtnStyle}>
          {t("Backend.Buttons.Check")} →
        </button>
      )}
      {slideIndex === 4 && (
        <button
          type="button"
          onClick={() => { formik.submitForm(); setSlideIndex(5); setFullScreen(true); }}
          style={{ ...nextBtnStyle, background: "linear-gradient(135deg,#51d28c,#2da866)", boxShadow: "0 4px 14px rgba(81,210,140,0.35)" }}
        >
          <FontAwesomeIcon icon={faWandMagicSparkles} style={{ marginRight: 6 }} />
          {t("Backend.Buttons.Plan")}
        </button>
      )}
      {slideIndex === 5 && schedules.length > 0 && (
        <button
          type="button"
          onClick={savePlan}
          style={{ ...nextBtnStyle, background: "linear-gradient(135deg,#51d28c,#2da866)", boxShadow: "0 4px 14px rgba(81,210,140,0.35)" }}
        >
          {t("Backend.Buttons.SavePlan")}
        </button>
      )}
    </div>
  );

  const nextBtnStyle = {
    padding: "8px 20px", borderRadius: 50, border: "none",
    background: "linear-gradient(135deg,#038edc,#0270b8)",
    color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer",
    boxShadow: "0 4px 14px rgba(3,142,220,0.3)",
  };

  return (
    <NewModal
      setShow={setShow}
      show={show}
      title={stepTitle}
      ExtraButton={NavButtons}
      onExit={() => {
        setSlideIndex(0);
        formik.resetForm();
        setSelectedPillars([]);
        setFullScreen(false);
        setSchedules([]);
      }}
      fullscreen={fullScreen}
    >
      {slideIndex < 5 && <StepBar current={slideIndex} total={5} />}

      {/* Step 0 — Month */}
      {slideIndex === 0 && (
        <div style={{ maxWidth: 400 }}>
          <SectionLabel>{t("Backend.Post.Assistant.AiPlanner.Month")}</SectionLabel>
          <DateField type="month" formik={formik} label="" valueName="month" />
          <div style={{ marginTop: 16 }}>
            <Timezones formik={formik} valueName="timezone" />
          </div>
        </div>
      )}

      {/* Step 1 — Interval */}
      {slideIndex === 1 && (
        <div style={{ maxWidth: 400 }}>
          <SectionLabel>{t("Backend.Post.Assistant.AiPlanner.PostingInterval")}</SectionLabel>
          <NumberField formik={formik} label="" valueName="interval" step="1" />
          <p style={{ color: "#9299a8", fontSize: 13, marginTop: 8 }}>
            How many times per week do you want to post?
          </p>
        </div>
      )}

      {/* Step 2 — Days */}
      {slideIndex === 2 && (
        <>
          <SectionLabel>Select up to {formik.values.interval || "—"} days</SectionLabel>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {daysOfWeek.map((day) => {
              const selected = (formik.values.days || []).includes(day);
              const disabled = !selected && (formik.values.days || []).length >= formik.values.interval;
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => toggleDay(day)}
                  disabled={disabled}
                  style={{
                    padding: "10px 20px", borderRadius: 50, fontSize: 14, fontWeight: 600,
                    cursor: disabled ? "not-allowed" : "pointer",
                    border: selected ? "2px solid #038edc" : "2px solid #e2e5ea",
                    background: selected ? "rgba(3,142,220,0.08)" : "#fff",
                    color: selected ? "#038edc" : disabled ? "#c8ccd4" : "#374151",
                    transition: "all 0.15s ease",
                  }}
                >
                  {selected && <FontAwesomeIcon icon={faCheck} style={{ marginRight: 6, fontSize: 11 }} />}
                  {t(`Days.${day}`)}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Step 3 — Pillars */}
      {slideIndex === 3 && (
        <>
          <SectionLabel>Select content pillars</SectionLabel>
          {pillars.length > 0 ? (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
              {pillars.map((pillar, index) => {
                const color = PILLAR_COLORS[index % PILLAR_COLORS.length];
                const selected = selectedPillars.some((p) => p.id === pillar.id);
                return (
                  <button
                    key={pillar.id}
                    type="button"
                    onClick={() => togglePillar(pillar)}
                    style={{
                      flex: "1 1 180px", padding: "16px 20px", borderRadius: 12, textAlign: "left",
                      border: `2px solid ${selected ? color : "#e2e5ea"}`,
                      background: selected ? `${color}10` : "#fff",
                      cursor: "pointer", transition: "all 0.15s ease",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <span style={{ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }} />
                      <span style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>{pillar.pillar}</span>
                      {selected && <FontAwesomeIcon icon={faCheck} style={{ color, fontSize: 11, marginLeft: "auto" }} />}
                    </div>
                    {pillar.description && (
                      <p style={{ fontSize: 12, color: "#6b7280", margin: 0, lineHeight: 1.5 }}>{pillar.description}</p>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "24px 0", color: "#9299a8" }}>
              <div style={{ width: 20, height: 20, borderRadius: "50%", border: "2px dotted #038edc", animation: "dotted-spin 0.9s linear infinite" }} />
              {t("Backend.Post.Assistant.AiPlanner.LoadingPillars")}
            </div>
          )}
        </>
      )}

      {/* Step 4 — Summary */}
      {slideIndex === 4 && (
        <>
          <SectionLabel>Review your plan</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 500 }}>
            {[
              { label: t("Backend.Post.Assistant.AiPlanner.Summary.Month"), value: formik.values.month, icon: "📅" },
              { label: t("Backend.Post.Assistant.AiPlanner.Summary.Week"), value: `${formik.values.interval}× per week`, icon: "🔁" },
              { label: t("Backend.Post.Assistant.AiPlanner.Summary.PostingDays"), value: (formik.values.days || []).join(", ") || "—", icon: "📆" },
              { label: t("Backend.Post.Assistant.AiPlanner.Summary.Pillars"), value: (formik.values.pillars || []).map((p) => p.pillar).join(", ") || "—", icon: "🏛️" },
            ].map(({ label, value, icon }) => (
              <div key={label} style={{
                display: "flex", alignItems: "flex-start", gap: 14,
                padding: "14px 16px", borderRadius: 12,
                background: "#f8fafc", border: "1px solid #eaecf0",
              }}>
                <span style={{ fontSize: 18, lineHeight: 1, flexShrink: 0 }}>{icon}</span>
                <div>
                  <p style={{ fontSize: 11, fontWeight: 700, color: "#9299a8", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 2 }}>{label}</p>
                  <p style={{ fontSize: 14, fontWeight: 600, color: "#111827", margin: 0 }}>{value}</p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Step 5 — Calendar result */}
      {slideIndex === 5 && (
        schedules.length > 0 ? (
          <PostCalendar
            schedules={schedules}
            setSchedules={setSchedules}
            customDeleteFunction={deletePlan}
            savePlan={savePlan}
            date={formik.values.month}
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 260, gap: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: "50%", border: "3px dotted #038edc", animation: "dotted-spin 0.9s linear infinite" }} />
            <p style={{ color: "#9299a8", fontSize: 14 }}>AI at work…</p>
          </div>
        )
      )}
    </NewModal>
  );
};

export default AiPlanningAssistantView;
